import {
    HttpErrorResponse,
    HttpEvent,
    HttpHandler,
    HttpInterceptor,
    HttpRequest,
    HttpStatusCode,
} from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { ofType } from '@ngrx/effects';
import { ActionsSubject, Store } from '@ngrx/store';
import { BehaviorSubject, EMPTY, from, Observable, throwError } from 'rxjs';
import { catchError, exhaustMap, filter, finalize, map, switchMap, take, tap, withLatestFrom } from 'rxjs/operators';
import {
    getRequestWithBaseApiUrl,
    isAuthLoginRequest,
    isConfigRequest,
    isDevicesRequest,
    isHealthRequest,
    isLocalRequest,
    isRfidLoginRequest,
    isTelemedicineRequest,
    isTokenRefreshRequest,
} from '@mona/api';
import { ConfigService } from '@mona/config';
import { Logger } from '@mona/shared/logger';
import { isEmpty, isNonNullable, notEmpty, Platform, PLATFORM } from '@mona/shared/utils';
import { AuthState, TokenStatus } from '../models';
import { AuthActions, AuthSelectors } from '../state';
import { AuthService } from './auth.service';
import { JwtHelperService } from './jwt-helper.service';
import { TokenStorageService } from './token-storage.service';

/**
 * Authorization token interceptor
 */
@Injectable()
export class TokenInterceptor implements HttpInterceptor {
    private logger = new Logger('AUTH:TOKENINTERCEPTOR');
    private refreshTokenInProgress = false;
    private refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);
    private refreshTokenStatus$ = this.store.select(AuthSelectors.selectAuthState).pipe(
        filter(
            auth =>
                auth.refreshTokenStatus === TokenStatus.INVALID ||
                (auth.refreshTokenStatus === TokenStatus.VALID && !!auth.user),
        ),
        map((auth: AuthState) => auth.refreshTokenStatus),
    );

    count = 0;

    // TODO: if a new user's token legitimately fails within 8s of login,
    // the 401 is swallowed. Proper fix: session state machine with abort
    // controller per session, not a time-based grace window.
    private static readonly POST_LOGOUT_SILENCE_MS = 8_000;

    /** Epoch ms until which a 401 is treated as "expected post-logout noise". */
    private postLogoutSilenceUntil = 0;

    /**
     * Constructor
     * @param platform
     * @param store
     * @param actionsObserver$
     * @param configService
     * @param tokenStorage
     * @param jwtHelper
     * @param authService
     */
    constructor(
        @Inject(PLATFORM) private platform: Platform,
        private store: Store<AuthState>,
        private actionsObserver$: ActionsSubject,
        private configService: ConfigService,
        private tokenStorage: TokenStorageService,
        private jwtHelper: JwtHelperService,
        private authService: AuthService,
    ) {
        this.actionsObserver$
            .pipe(
                ofType(AuthActions.logOut, AuthActions.logInWithRfid, AuthActions.logInWithCredentials),
                withLatestFrom(this.store.select(AuthSelectors.selectIsAuthenticated)),
                filter(([action, isAuthenticated]) => action.type === AuthActions.logOut.type || isAuthenticated),
            )
            .subscribe(() => {
                this.postLogoutSilenceUntil = Date.now() + TokenInterceptor.POST_LOGOUT_SILENCE_MS;
                this.logger.log(
                    'started post-logout/user-switch 401 silence window',
                    TokenInterceptor.POST_LOGOUT_SILENCE_MS,
                );
            });
    }

    /** True if we are inside the post-logout grace window during which 401s are expected. */
    private isInPostLogoutSilence(): boolean {
        return Date.now() < this.postLogoutSilenceUntil;
    }

    /**
     * Identifies and handles a given HTTP request by adding `Authorization` header
     * @param request request
     * @param next next
     */
    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        if (isLocalRequest(request) || isConfigRequest(request)) {
            return next.handle(request);
        }
        return this.configService.configLoaded$.pipe(
            exhaustMap(() => {
                const config = this.configService.config;
                if (isEmpty(config)) {
                    return next.handle(request);
                }
                if (this.skipTokenHeader(request)) {
                    return next.handle(getRequestWithBaseApiUrl(request, config));
                }
                const req = getRequestWithBaseApiUrl(request, config);
                return from(this.getAccessToken(req)).pipe(
                    switchMap(accessToken => {
                        const authRequest = this.addTokenHeader(req, accessToken);
                        return next.handle(authRequest);
                    }),
                    catchError(err => {
                        // in case of 401 http error
                        if (err instanceof HttpErrorResponse && err.status === HttpStatusCode.Unauthorized) {
                            if (this.isInPostLogoutSilence()) {
                                this.logger.log(
                                    'suppressing 401 during post-logout grace window',
                                    (request as any).url,
                                );
                                return EMPTY;
                            }

                            // get refresh tokens
                            const refreshToken = this.tokenStorage.getRefreshToken();

                            // if there are tokens then send refresh token request
                            if (isNonNullable(refreshToken) && !this.jwtHelper.isTokenExpired(refreshToken)) {
                                this.logger.warn(
                                    '401 received; refreshing token and retrying request',
                                    this.describeRequest(req),
                                    this.getTokenSummary('refresh', refreshToken),
                                );
                                return this.refreshToken(req, next);
                            }

                            // otherwise logout and redirect to login page
                            this.logger.warn(
                                '401 received without usable refresh token; logging out',
                                this.describeRequest(req),
                                this.getTokenSummary('refresh', refreshToken),
                            );
                            return this.logOut(err);
                        }

                        // in case of 403 http error (refresh token failed)
                        if (err instanceof HttpErrorResponse && err.status === HttpStatusCode.Forbidden) {
                            if (this.isInPostLogoutSilence()) {
                                this.logger.log(
                                    'suppressing 403 during post-logout grace window',
                                    (request as any).url,
                                );
                                return EMPTY;
                            }

                            // logout and redirect to login page
                            return this.logOut(err);
                        }
                        // if error has status neither 401 nor 403 then just return this error
                        return throwError(err);
                    }),
                );
            }),
        );
    }

    private addTokenHeader(request: HttpRequest<any>, token: string) {
        return request.clone({ setHeaders: { Authorization: `Bearer ${token}` } });
    }

    /**
     *  Do not add token for these request urls
     * @param request
     */
    private skipTokenHeader(request?: HttpRequest<any>): boolean {
        return (
            isDevicesRequest(request) ||
            isHealthRequest(request) ||
            isConfigRequest(request) ||
            isAuthLoginRequest(request) ||
            isTokenRefreshRequest(request)
        );
    }

    /**
     * Auth token getter - return either user token or device token
     * Skip user token if req is RFID login OR if req is for Telemedicine server
     *  @param request
     */
    private async getAccessToken(request: HttpRequest<any>): Promise<string> {
        const userToken = this.tokenStorage.getAccessToken(),
            rfidLoginRequest = isRfidLoginRequest(request),
            telemedicineRequest = isTelemedicineRequest(request);
        if (!this.platform.isElectron || (!isEmpty(userToken) && !rfidLoginRequest && !telemedicineRequest)) {
            return Promise.resolve(userToken);
        } else {
            // logger.debug(`TokenGetterFactory: use DEVICE token for "${req?.url || 'anything'}"`);
            return this.authService.getDeviceAccessToken(telemedicineRequest);
        }
    }

    private refreshToken(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        if (!this.refreshTokenInProgress) {
            this.refreshTokenInProgress = true;
            this.refreshTokenSubject.next(null);

            this.logger.log('refresh token request started', this.describeRequest(request));
            this.store.dispatch(AuthActions.refreshTokenRequest());
            return this.actionsObserver$.pipe(
                ofType(AuthActions.refreshTokenSuccess, AuthActions.refreshTokenFailure),
                take(1),
                switchMap(action => {
                    if (action.type === AuthActions.refreshTokenFailure.type) {
                        this.logger.error(
                            'refresh token request failed while retrying original request',
                            this.describeRequest(request),
                            (action as any).error,
                        );
                        return this.logOut((action as any).error);
                    }

                    const accessToken = (action as any).token;
                    this.refreshTokenSubject.next(accessToken);
                    this.logger.log(
                        'refresh token request succeeded; retrying original request',
                        this.describeRequest(request),
                        this.getTokenSummary('access', accessToken),
                    );
                    // repeat failed request with new token
                    return next.handle(this.addTokenHeader(request, accessToken));
                }),
                finalize(() => (this.refreshTokenInProgress = false)),
            );
        } else {
            this.logger.log('refresh token already in progress; queueing request retry', this.describeRequest(request));
            // wait while getting new token
            return this.refreshTokenSubject.pipe(
                filter(token => token !== null),
                take(1),
                tap(token =>
                    this.logger.log(
                        'refresh token completed; retrying queued request',
                        this.describeRequest(request),
                        this.getTokenSummary('access', token),
                    ),
                ),
                switchMap(token => {
                    // repeat failed request with new token
                    return next.handle(this.addTokenHeader(request, token));
                }),
            );
        }
    }

    private logOut(err): Observable<HttpEvent<any>> {
        this.logger.log('logoutAndRedirect', err);
        this.authService.logOut();
        return throwError(err);
    }

    private describeRequest(request: HttpRequest<any>): string {
        return `${request.method} ${request.urlWithParams || request.url}`;
    }

    private getTokenSummary(name: string, token?: string): string {
        if (!token) {
            return `${name}=missing`;
        }

        try {
            const expiresAt = this.jwtHelper.getTokenExpirationDate(token);
            if (!expiresAt) {
                return `${name}=present exp=unknown`;
            }

            const remainingMs = expiresAt.getTime() - Date.now();
            return `${name}=present exp=${expiresAt.toISOString()} remainingMs=${remainingMs}`;
        } catch (error) {
            return `${name}=present exp=invalid`;
        }
    }
}
